import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, thumbUrl } from "@/lib/api";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Facebook } from "lucide-react";
import { toast } from "sonner";

const SYM = { gbp: "£", usd: "$", eur: "€" };

function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function PhotographerHighlights() {
  const { slug } = useParams();
  const site = useSiteConfig();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/photographers/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="highlights-error">
        <div className="text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl">Photographer not found</h1>
          <p className="text-white/50 text-sm">{error}</p>
          <Link to="/events" className="inline-block bg-[var(--pm-orange)] text-black px-8 py-3 text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)]">
            Browse events
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="highlights-loading">
        <p className="text-white/50 text-sm uppercase tracking-[0.3em]">Loading highlights…</p>
      </div>
    );
  }

  const { photographer, stats, highlights } = data;
  const promos = data.promo_codes || [];

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`Code ${code} copied — enter it at checkout`); } catch { /* clipboard unavailable */ }
  };

  const shareUrl = `https://${site.site_domain}/p/${slug}`;
  const shareText = `${photographer.name}'s ${site.vertical_noun} photos on ${site.siteName}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} — ${shareUrl}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="highlights-page">
      <div className="max-w-6xl mx-auto px-6 py-16 space-y-12">
        <header className="space-y-4">
          <p className="text-xs uppercase tracking-[0.4em] pm-orange font-bold">Photographer highlights</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl" data-testid="highlights-name">{photographer.name}</h1>
          <div className="flex flex-wrap gap-8 pt-2">
            <div>
              <p className="font-display text-3xl uppercase" data-testid="stat-live">{stats.live_photos}</p>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Live photos</p>
            </div>
            <div>
              <p className="font-display text-3xl uppercase pm-orange" data-testid="stat-sold">{stats.photos_sold}</p>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Photos sold</p>
            </div>
            {photographer.website && (
              <a
                href={photographer.website.startsWith("http") ? photographer.website : `https://${photographer.website}`}
                target="_blank" rel="noreferrer"
                className="self-end text-xs uppercase tracking-[0.3em] text-white/60 hover:pm-orange underline underline-offset-4"
                data-testid="highlights-website"
              >
                Website
              </a>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-4" data-testid="highlights-share">
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">Share</span>
            <a
              href={waHref}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:border-[#25D366] hover:text-[#25D366] transition-colors"
              data-testid="share-whatsapp"
            >
              <WhatsAppIcon /> WhatsApp
            </a>
            <a
              href={fbHref}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:border-[#1877F2] hover:text-[#1877F2] transition-colors"
              data-testid="share-facebook"
            >
              <Facebook size={14} /> Facebook
            </a>
          </div>
        </header>

        {promos.length > 0 && (
          <section className="border-2 border-[var(--pm-orange)] bg-[var(--pm-orange)]/5 p-6 space-y-4" data-testid="highlights-promos">
            <p className="text-xs uppercase tracking-[0.4em] pm-orange font-bold">Discount codes from {photographer.name}</p>
            <div className="flex flex-wrap gap-3">
              {promos.map((p) => (
                <button
                  key={p.code}
                  onClick={() => copyCode(p.code)}
                  className="group text-left border border-white/20 bg-black px-5 py-4 hover:border-[var(--pm-orange)] transition-colors"
                  title="Tap to copy"
                  data-testid={`promo-code-${p.code}`}
                >
                  <p className="font-display text-3xl uppercase tracking-widest pm-orange">{p.code}</p>
                  <p className="text-sm text-white/80 font-condensed">{p.description}</p>
                  {p.limits && <p className="text-xs text-white/40 font-condensed mt-1">{p.limits}</p>}
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2 group-hover:text-white/70">Tap to copy · use at checkout</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {highlights.length === 0 ? (
          <p className="text-white/50 text-sm font-condensed" data-testid="no-highlights">
            No live photos right now — check back after the next race weekend.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="highlights-grid">
            {highlights.map((img, idx) => {
              const sym = SYM[(img.currency || "gbp").toLowerCase()] || "";
              return (
                <Link
                  key={img.id}
                  to={`/image/${img.id}`}
                  className="group border border-white/10 bg-[#0a0a0a] hover:border-[var(--pm-orange)] transition-colors"
                  data-testid={`highlight-card-${img.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={thumbUrl(img.id)}
                      alt={img.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {idx === 0 && (img.sold_count || 0) > 0 && (
                      <span className="absolute top-2 left-2 bg-[var(--pm-orange)] text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1">
                        Best seller
                      </span>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <p className="text-sm truncate">{img.title}</p>
                    <div className="flex justify-between text-xs text-white/40 font-condensed">
                      <span className="truncate">{img.event_name || ""}</span>
                      <span className="pm-orange font-semibold whitespace-nowrap">{sym}{(img.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <footer className="border-t border-white/10 pt-8">
          <Link to="/events" className="text-xs uppercase tracking-[0.3em] text-white/60 hover:pm-orange" data-testid="highlights-browse-all">
            Browse all events →
          </Link>
        </footer>
      </div>
    </div>
  );
}
